import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AsyncSelect, Button, Modal } from '@getro/rombo';
import { useFocusTrapState } from 'hooks/useFocusTrapState';

const statusLabel = (status) => status && `${status.charAt(0).toUpperCase()}${status.slice(1)}`;

const jobOption = (job, recommended = false) => ({
  label: [job.title, job.company?.name, statusLabel(job.status), recommended && 'Recommended']
    .filter(Boolean)
    .join(' — '),
  value: job.id ?? job.jobId,
});

const uniqueOptions = (options) => [...new Map(options.map((option) => [option.value, option])).values()];
const SELECT_COMPONENTS = { LoadingIndicator: () => null };

export const JobLinkModal = ({ project, searchJobs, onCancel, onSave, onRemove, saving, removing }) => {
  const { pauseFocusTrap, onDropDownStateChange } = useFocusTrapState();
  const defaultOptions = useMemo(
    () =>
      uniqueOptions([
        ...(project.job ? [jobOption(project.job)] : []),
        ...(project.matchSuggestions || []).map((suggestion) => jobOption(suggestion, true)),
      ]),
    [project],
  );
  const initialValue = defaultOptions.find(({ value }) => value === project.job?.id) || null;
  const [selectedJob, setSelectedJob] = useState(initialValue);
  const [searchState, setSearchState] = useState({ error: false, hasMore: false, hasSearched: false });
  const requestId = useRef(0);
  const pendingSearch = useRef(null);

  const performSearch = async (query) => {
    const currentRequestId = requestId.current + 1;
    requestId.current = currentRequestId;
    try {
      const response = await searchJobs(query);
      if (requestId.current === currentRequestId) {
        setSearchState({ error: false, hasMore: Boolean(response.meta?.hasMore), hasSearched: true });
      }
      return uniqueOptions((response.jobs || []).map((job) => jobOption(job)));
    } catch (_error) {
      if (requestId.current === currentRequestId) {
        setSearchState({ error: true, hasMore: false, hasSearched: true });
      }
      return [];
    }
  };
  const cancelPendingSearch = () => {
    if (!pendingSearch.current) return;
    clearTimeout(pendingSearch.current.timer);
    pendingSearch.current.resolve([]);
    pendingSearch.current = null;
  };

  useEffect(() => () => cancelPendingSearch(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOptions = (inputValue) => {
    const query = inputValue.trim();
    cancelPendingSearch();
    if (query.length < 2) {
      requestId.current += 1;
      setSearchState({ error: false, hasMore: false, hasSearched: false });
      return Promise.resolve(defaultOptions);
    }

    setSearchState({ error: false, hasMore: false, hasSearched: true });
    return new Promise((resolve) => {
      const timer = setTimeout(async () => {
        pendingSearch.current = null;
        resolve(await performSearch(query));
      }, 400);
      pendingSearch.current = { resolve, timer };
    });
  };

  const noOptionsMessage = ({ inputValue }) => {
    if (searchState.error) return 'We couldn’t search jobs. Please try again.';
    if (inputValue.trim().length < 2) return 'Enter at least 2 characters.';
    return 'No matching jobs found.';
  };

  const selectJob = (option) => {
    // A portalled async menu can emit a transient null while focus returns to
    // the modal. Keep the option the customer just chose instead of clearing it.
    if (option) setSelectedJob(option);
  };

  return (
    <Modal
      title={`Link “${project.name}” to a job`}
      isOpen
      onCancel={onCancel}
      pauseFocusTrap={pauseFocusTrap}
      actions={
        <div className="TalentAttraction__modal-actions">
          {project.job && (
            <Button variant="tertiary" onClick={onRemove} loading={removing}>
              Remove link
            </Button>
          )}
          <div className="TalentAttraction__modal-primary-actions">
            <Button variant="tertiary" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={() => onSave(selectedJob?.value)} disabled={!selectedJob} loading={saving}>
              Link job
            </Button>
          </div>
        </div>
      }
    >
      <div className="TalentAttraction__modal-content">
        <p>
          Link this outreach project to its matching job to compare outreach results with activity on your job board.
        </p>
        <span className="TalentAttraction__modal-label">Job</span>
        <AsyncSelect
          aria-label="Job"
          anchorToBody
          components={SELECT_COMPONENTS}
          debounceTime={0}
          defaultOptions={defaultOptions}
          loadOptions={loadOptions}
          loadingMessage={() => 'Searching jobs…'}
          noOptionsMessage={noOptionsMessage}
          isClearable={false}
          value={selectedJob}
          onChange={selectJob}
          onMenuOpen={() => onDropDownStateChange('open')}
          onMenuClose={() => onDropDownStateChange('close')}
          placeholder="Search by job title or company"
          position="auto"
        />
        <p
          aria-live="polite"
          className={`TalentAttraction__job-search-help${
            searchState.error ? ' TalentAttraction__job-search-help--error' : ''
          }`}
        >
          {searchState.error && 'Job search is temporarily unavailable. Your dashboard is still available.'}
          {!searchState.error &&
            searchState.hasMore &&
            'Showing the first 25 matches. Keep typing to narrow the results.'}
          {!searchState.error &&
            !searchState.hasMore &&
            searchState.hasSearched &&
            'Search results update as you type.'}
          {!searchState.error &&
            !searchState.hasMore &&
            !searchState.hasSearched &&
            'Enter at least 2 characters to search by job title or company.'}
        </p>
      </div>
    </Modal>
  );
};

JobLinkModal.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    job: PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      status: PropTypes.string,
      company: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    }),
    matchSuggestions: PropTypes.arrayOf(
      PropTypes.shape({
        jobId: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        status: PropTypes.string,
      }),
    ),
  }).isRequired,
  searchJobs: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  removing: PropTypes.bool.isRequired,
};
